/*
  Canvas by Numbers ES - regalos gratis en el carrito (sin app)
  Ported from canvasbynumbers.com (theme "Free Gifts in Cart 09-30").

  Config: window.cbnGifts, printed by snippets/cbn-gift-config.liquid from
  Theme settings > Free gifts in cart:
    { gifts: [{ variant, group: 'pbn' | 'dp', min, label }], state: {...} }

  How it works
  - Ecomify redraws the cart drawer / cart page through window.refreshCartContents(),
    which fires `updated.ecomify.cart` on window when done. We listen to that.
  - After every cart change we read /cart?view=cbn-gift (templates/cart.cbn-gift.liquid),
    which returns the kit counts (by product tag) and every line, then:
      * add each gift whose group has reached its minimum (line property _cbn_gift)
      * remove gifts the cart no longer qualifies for
      * trim duplicate gift lines back to quantity 1
      * remove any stray "Free Gift" line that is not a configured gift
  - A gift the shopper removes is remembered as declined for the session
    (sessionStorage) and is not re-added. Declined ids are written to
    <html data-cbn-gift-declined="..."> so CSS can show the "+ Añadir tu regalo" button.
  - Every cart write on the page (theme, upsells, this script) runs through ONE queue,
    window.cbnCartQueue, by wrapping window.fetch for POST /cart/add|change|update|clear.
    Shopify applies simultaneous cart writes as "last write wins", so without the queue
    an add-on sent at the same moment as a gift could be lost.
  - A busy flag plus a single re-check once finished stops our own changes from looping.
*/
(() => {
  const CFG = window.cbnGifts
  if (!CFG || !Array.isArray(CFG.gifts) || !CFG.gifts.length) return

  const DECLINED_KEY = 'cbn_gift_declined'
  const PROP = '_cbn_gift'
  const PROP_VALUE = 'Regalo'
  const LOG = (...a) => { if (window.cbnGiftDebug) console.log('[cbn-gift]', ...a) }

  /* ---------- 1. Shared cart write queue ---------- */
  if (!window.cbnCartQueue) {
    const nativeFetch = window.fetch.bind(window)
    let tail = Promise.resolve()
    const run = (task) => {
      const p = tail.then(task, task)
      tail = p.then(() => {}, () => {})
      return p
    }
    const WRITE_RE = /\/cart\/(add|change|update|clear)(\.js|\.json)?(\?|$)/
    window.fetch = function (input, init) {
      try {
        const url = typeof input === 'string' ? input : (input && input.url) || String(input || '')
        const method = ((init && init.method) || (input && input.method) || 'GET').toUpperCase()
        if (method === 'POST' && WRITE_RE.test(url.split('#')[0])) {
          return run(() => nativeFetch(input, init))
        }
      } catch (e) { /* fall through */ }
      return nativeFetch(input, init)
    }
    window.cbnCartQueue = { run, fetch: nativeFetch }
  }
  const queue = window.cbnCartQueue

  /* ---------- 2. Declined gifts (session) ---------- */
  const readDeclined = () => {
    try { return JSON.parse(sessionStorage.getItem(DECLINED_KEY) || '[]').map(Number) } catch (e) { return [] }
  }
  const paintDeclined = (list) => {
    if (list.length) document.documentElement.setAttribute('data-cbn-gift-declined', list.join(' '))
    else document.documentElement.removeAttribute('data-cbn-gift-declined')
  }
  const writeDeclined = (list) => {
    list = [...new Set(list.map(Number))]
    try { sessionStorage.setItem(DECLINED_KEY, JSON.stringify(list)) } catch (e) { /* private mode */ }
    paintDeclined(list)
  }
  let declined = readDeclined()
  paintDeclined(declined)

  /* ---------- 3. Cart helpers ---------- */
  const giftIds = new Set(CFG.gifts.map((g) => Number(g.variant)))
  const failed = new Set() // gifts that could not be added on this page view

  const readState = async () => {
    // No "Accept: application/json" header here: with it Shopify answers with /cart.js
    // instead of rendering the cbn-gift view.
    const res = await queue.run(() => queue.fetch(`/cart?view=cbn-gift&_=${Date.now()}`, {
      credentials: 'same-origin'
    }))
    const text = await res.text()
    return JSON.parse(text)
  }
  const post = (path, body) => fetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body)
  })
  // The gift add is not a shopper action, so tracking must not count it as an "add to cart".
  // Shopify analytics, Google and Klaviyo watch window.fetch for POST /cart/add.js. A fetch taken
  // from a hidden same-origin iframe is one they have not wrapped, so they never see this request.
  // (Same as canvasbynumbers.com 10-09.)
  let quiet = null
  const quietFetch = () => {
    if (quiet) return quiet
    try {
      const frame = document.createElement('iframe')
      frame.hidden = true
      frame.tabIndex = -1
      frame.title = 'cbn-gift'
      frame.setAttribute('aria-hidden', 'true')
      document.body.appendChild(frame)
      quiet = frame.contentWindow.fetch.bind(frame.contentWindow)
    } catch (e) {
      quiet = window.fetch.bind(window)
    }
    return quiet
  }
  const addGift = (variant) => quietFetch()(`${location.origin}/cart/add.js`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ items: [{ id: variant, quantity: 1, properties: { [PROP]: PROP_VALUE } }] })
  })
  const setQty = (key, quantity) => post('/cart/change.js', { id: key, quantity })

  /* ---------- 4. Decide what has to change ---------- */
  const plan = (state) => {
    const ops = []
    const counts = { pbn: state.pbn || 0, dp: state.dp || 0 }
    const giftLines = (state.lines || []).filter((l) => l.gift || giftIds.has(Number(l.variant_id)))

    // Stray gift lines that are not a configured gift
    giftLines
      .filter((l) => !giftIds.has(Number(l.variant_id)))
      .forEach((l) => ops.push({ what: 'remove stray', run: () => setQty(l.key, 0) }))

    CFG.gifts.forEach((g) => {
      const v = Number(g.variant)
      const lines = giftLines.filter((l) => Number(l.variant_id) === v)
      const qualifies = (counts[g.group] || 0) >= Number(g.min || 1)
      const wanted = qualifies && !declined.includes(v)

      if (!wanted) {
        lines.forEach((l) => ops.push({ what: `remove ${g.label}`, run: () => setQty(l.key, 0) }))
        return
      }
      if (!lines.length) {
        if (!failed.has(v)) ops.push({ what: `add ${g.label}`, gift: v, run: () => addGift(v) })
        return
      }
      // Keep one line at quantity 1
      lines.slice(1).forEach((l) => ops.push({ what: `trim ${g.label}`, run: () => setQty(l.key, 0) }))
      if (Number(lines[0].quantity) !== 1) {
        ops.push({ what: `qty1 ${g.label}`, run: () => setQty(lines[0].key, 1) })
      }
    })
    return ops
  }

  // Quick check against the state printed in the page, to skip a request on page load
  const looksInSync = (state) => plan(state).length === 0

  /* ---------- 5. Sync ---------- */
  let busy = false
  let again = false

  const redraw = async () => {
    if (typeof window.refreshCartContents !== 'function') return
    // This redraw only brings in (or takes out) the gift line, so it should not look like a
    // second cart update: no dimming (class cbn-quiet, CSS in cbn-gift-config.liquid) and the
    // "Item added" note from the shopper's own add stays instead of "Cart was updated".
    const drawer = document.querySelector('#offcanvas-cart')
    const shown = drawer && drawer.querySelector('#offcanvas-cart-alert-add:not([hidden])')
    const noteHtml = shown ? shown.innerHTML : null
    if (drawer) drawer.classList.add('cbn-quiet')
    try {
      // A synthetic OK response: refreshCartContents shows its "Cart updated" note
      await window.refreshCartContents(new Response('{}', { status: 200 }))
    } finally {
      if (drawer) drawer.classList.remove('cbn-quiet')
    }
    if (noteHtml !== null) {
      const add = drawer.querySelector('#offcanvas-cart-alert-add')
      const updated = drawer.querySelector('#offcanvas-cart-alert-updated')
      if (add) { add.innerHTML = noteHtml; add.removeAttribute('hidden') }
      if (add && updated) updated.setAttribute('hidden', 'hidden')
    }
  }

  const sync = async (reason) => {
    if (busy) { again = true; return }
    busy = true
    let changed = false
    try {
      for (let pass = 0; pass < 3; pass++) {
        const state = await readState()
        const ops = plan(state)
        LOG(reason, 'pass', pass, state, ops.map((o) => o.what))
        if (!ops.length) break
        for (const op of ops) {
          const res = await op.run()
          if (!res.ok && op.gift) failed.add(op.gift)
          changed = true
        }
      }
      if (changed) await redraw()
    } catch (e) {
      console.warn('[cbn-gift]', e)
    } finally {
      busy = false
      if (again) {
        again = false
        setTimeout(() => sync('re-check'), 0)
      }
    }
  }

  window.cbnGiftSync = sync

  window.addEventListener('updated.ecomify.cart', () => sync('cart updated'))

  /* ---------- 6. Shopper removes / re-adds a gift ---------- */
  document.addEventListener('click', (e) => {
    const remove = e.target.closest('[data-cbn-gift-remove]')
    if (remove) {
      const v = Number(remove.getAttribute('data-cbn-gift-remove'))
      if (v) writeDeclined([...readDeclined(), v])
      declined = readDeclined()
      return // the theme's own onRemoveCartItem does the removal
    }
    const add = e.target.closest('[data-cbn-gift-add]')
    if (add) {
      e.preventDefault()
      const v = Number(add.getAttribute('data-cbn-gift-add'))
      writeDeclined(readDeclined().filter((id) => id !== v))
      declined = readDeclined()
      failed.delete(v)
      add.setAttribute('disabled', 'disabled')
      sync('re-add')
    }
  }, true)

  /* ---------- 7. First run ---------- */
  const start = () => {
    const s = CFG.state
    if (s && !s.count) return // empty cart, nothing to do
    if (s && looksInSync(s)) return
    sync('page load')
  }
  if ('requestIdleCallback' in window) requestIdleCallback(start, { timeout: 2000 })
  else setTimeout(start, 500)
})()
